import React, { useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { useUserAlias } from 'src/hooks/useUserAlias';

const APPLICATION_ID = '3ce9d434-2143-4a75-a3a0-b3c699e34e6e';
const APPLICATION_VERSION = '1.0.0';
const GUEST_ROLE_ARN = 'arn:aws:iam::567500875613:role/FinSpace-RUM-gamma-CognitoDefaultUnauthenticatedRol-3fGhCTq8xTwE';
const IDENTITY_POOL_ID = 'us-west-2:3a47b295-ae3e-4c68-9d27-cf871dfe7e4b';
const RUM_ENDPOINT = 'https://dataplane.rum.us-west-2.amazonaws.com';
const APPLICATION_REGION = 'us-west-2';

let awsRum: AwsRum | null = null;

interface RumProviderProps {
  children: React.ReactNode;
}

export const RumProvider: React.FC<RumProviderProps> = ({ children }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const { alias } = useUserAlias();

  useEffect(() => {
    if (!awsRum && APPLICATION_ID && !APPLICATION_ID.includes('{{')) {
      initializeRum(!!user, alias);
    } else if (awsRum && user) {
      awsRum.addSessionAttributes({
        isAuthenticated: 'true',
        userAlias: alias,
      });
    }
  }, [user, alias]);

  return <>{children}</>;
};

function initializeRum(isAuthenticated: boolean, alias: string) {
  const config: AwsRumConfig = {
    sessionAttributes: {
      isAuthenticated: isAuthenticated ? 'true' : 'false',
      userAlias: alias || 'guest',
    },
    sessionSampleRate: 1,
    guestRoleArn: GUEST_ROLE_ARN,
    identityPoolId: IDENTITY_POOL_ID,
    endpoint: RUM_ENDPOINT,
    telemetries: ['performance', 'errors', 'http', 'interaction'],
    allowCookies: true,
    enableXRay: true,
    cookieAttributes: {
      domain: window.location.hostname,
      path: '/',
      sameSite: 'Lax',
      secure: true,
      unique: true,
    },
  };

  try {
    awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    window.addEventListener('unhandledrejection', (event) => {
      awsRum?.recordError(event.reason);
    });
  } catch (error) {
    console.error('Failed to initialize CloudWatch RUM:', error);
  }
}

export const recordCustomEvent = (name: string, data?: Record<string, unknown>): void => {
  try {
    awsRum?.recordEvent(name, data || {});
  } catch (error) {
    console.error('Failed to record custom event:', error);
  }
};

export const recordError = (error: Error): void => {
  try {
    awsRum?.recordError(error);
  } catch (err) {
    console.error('Failed to record error:', err);
  }
};

export const addSessionAttributes = (
  attributes: Record<string, string | number | boolean>
): void => {
  try {
    awsRum?.addSessionAttributes(attributes);
  } catch (error) {
    console.error('Failed to add session attributes:', error);
  }
};
